import type {
  CallFilter,
  CallOutcomeOption,
  CallReasonType,
  CallRow,
  CallsTodayStats,
} from './types';

/* ---- reason type meta (tag colours, board §1) ---------------------------- */

export const CALL_REASON_META: Record<CallReasonType, { label: string; fg: string; bg: string; href: (id: string) => string; linkLabel: string }> = {
  treatment: { label: 'Treatment', fg: '#3742b0', bg: '#edeffc', href: (id) => `/patients/${id}/treatments`, linkLabel: 'Cheat sheet' },
  invisalign: { label: 'Invisalign', fg: '#6b3fa0', bg: '#f1ebfa', href: (id) => `/patients/${id}/invisalign`, linkLabel: 'Invisalign' },
  balance: { label: 'Balance', fg: '#8a5a12', bg: '#fbf1de', href: (id) => `/patients/${id}/profile#balance`, linkLabel: 'Balance' },
  recall: { label: 'Recall', fg: '#1b5f45', bg: '#eef6f2', href: (id) => `/patients/${id}/profile`, linkLabel: 'Recall' },
};

export const CALLS_TODAY_STATS: CallsTodayStats = { due: 11, called: 6, noAnswer: 2, booked: 3, paid: '$390' };

/* ---- the outcome pick-lists (§5.2 / §6) ---------------------------------- */

const MONEY_OUTCOMES: CallOutcomeOption[] = [
  { key: 'paid', label: 'Paid on the call', effect: 'That reason leaves.' },
  { key: 'link-texted', label: 'Will pay — link texted', effect: 'That reason leaves; A/R texts the link now and waits 3 days.' },
  { key: 'callback', label: 'Call back on…', effect: 'Moves to Later until the date.' },
  { key: 'disputes', label: 'Disputes it', effect: 'Leaves; a Triage task for the billing coordinator.', red: true },
];

const CARE_OUTCOMES: CallOutcomeOption[] = [
  { key: 'booked', label: 'Booked', effect: 'That reason leaves.' },
  { key: 'thinking', label: 'Thinking about it', effect: 'Leaves; its agent brings it back after its wait if nothing happens.' },
  { key: 'callback', label: 'Call back on…', effect: 'Moves to Later until the date.' },
  { key: 'not-interested', label: 'Not interested', effect: 'Leaves for good; that agent stops on that topic.', red: true },
];

export function callOutcomesFor(type: CallReasonType): CallOutcomeOption[] {
  return type === 'balance' ? MONEY_OUTCOMES : CARE_OUTCOMES;
}

export const CALL_REACH_OPTIONS: CallOutcomeOption[] = [
  { key: 'reached', label: 'Reached them', effect: '' },
  { key: 'no-answer', label: 'No answer', effect: 'Stays; back tomorrow. After 3 tries in 7 days it leaves.' },
  { key: 'voicemail', label: 'Left a voicemail', effect: 'Stays; back tomorrow. After 3 tries in 7 days it leaves.' },
  { key: 'wrong-number', label: 'Wrong number', effect: 'Every reason leaves; the number is flagged.', red: true },
];

/** The folded Invisalign questions (spec 30 §5), reused on an Invisalign row. */
export const CALL_INVIS_SMILE = ['Overbite', 'Underbite', 'Crossbite', 'Gap teeth', 'Open bite', 'Crooked teeth', 'Generally straight', 'Mixed baby teeth'];
export const CALL_INVIS_CONCERNS = ['Cost', 'Insurance', 'Monthly payments', 'Discomfort', 'How long it takes', 'How it looks', 'Eating and cleaning'];
export const CALL_INVIS_EVENTS = ['Wedding', 'Graduation', 'Photos or a big event', 'Other'];
export const CALL_INVIS_DECIDES = ['Just them', 'Partner', 'Parent'];

/* ---- per-type default What-to-say, for the lighter rows ------------------ */

const DEFAULT_SAY: Record<CallReasonType, { whatToSay: { label: string; body: string }[]; dont: string }> = {
  treatment: {
    whatToSay: [
      { label: 'Open with', body: 'The tooth the dentist flagged, not the price.' },
      { label: 'The money', body: 'Insurance covers about half; there’s benefit left this year.' },
    ],
    dont: 'promise an exact price · don’t add anything clinical Dr. Carter hasn’t written',
  },
  invisalign: {
    whatToSay: [
      { label: 'Open with', body: 'Their question on cost or insurance.' },
      { label: 'The money', body: 'They have unused orthodontic benefit; a consult gives the exact number.' },
    ],
    dont: 'quote a case fee · don’t say “good candidate” · don’t promise results or a finish date',
  },
  balance: {
    whatToSay: [{ label: 'Say', body: 'What’s left and that you can text the payment link now.' }],
    dont: 'mention collections, credit or anything that sounds like a threat',
  },
  recall: {
    whatToSay: [{ label: 'Say', body: 'They’re due for a cleaning and you can find a time now.' }],
    dont: 'imply anything is wrong · don’t pressure',
  },
};

const say = (type: CallReasonType) => DEFAULT_SAY[type];

/* ---- the 24 rows (§1) ---------------------------------------------------- */

export const CALL_ROWS: CallRow[] = [
  {
    id: 'maria-lopez', name: 'Maria Lopez', phone: '(210) 555-0133', askedAt: 'asked 9:12 am',
    cardIdentity: '41 · since 2019 · Cigna PPO · asked to be called', group: 'today', tries: [],
    reasons: [{
      type: 'treatment', asked: true, title: 'Implant crown', quote: 'Call me',
      whyLine: 'Tapped {q} on her plan page — an implant crown, $1,640',
      chips: ['$1,640 · she pays ~$610', '$2,000 benefits left'],
      whatToSay: [{ label: 'Open with', body: 'She asked to be called about the implant crown.' }, { label: 'The money', body: 'Insurance covers about half; she has benefit left this year.' }],
      dont: say('treatment').dont,
    }],
    timeline: [{ at: '30 Sep 9:12a', body: 'Tapped Call me on her plan page' }, { at: '27 Sep', body: 'Opened her plan · viewed the estimate' }],
  },
  {
    id: 'denise-park', name: 'Denise Park', phone: '(210) 555-0176', askedAt: 'asked 8:40 am',
    cardIdentity: '58 · since 2014 · Delta Dental PPO · asked to be called', group: 'today', tries: [],
    reasons: [{
      type: 'balance', asked: true, title: '$265 balance',
      whyLine: 'Asked the AI Receptionist for a call about her $265 balance',
      chips: ['$265 · cleaning 12 Aug', '48 days'],
      whatToSay: [{ label: 'Say', body: '“There’s $265 left from your August cleaning — I can text you the payment link now.”' }],
      dont: say('balance').dont,
    }],
    timeline: [{ at: '30 Sep 8:40a', body: 'Asked the AI Receptionist for a call about the balance' }],
  },
  {
    id: 'todd-baird', name: 'Todd Baird', phone: '(415) 555-0142', bestTime: 'best in the evening', language: 'Farsi',
    cardIdentity: '52 · since 2016 · Sun Life PPO · best in the evening · speaks Farsi', group: 'today', tries: [],
    reasons: [
      {
        type: 'treatment', title: 'Crown #14 and a deep cleaning', quote: 'Is the crown covered now?',
        whyLine: 'Replied 28 Sep: {q} · crown #14 + deep cleaning · $2,130',
        chips: ['$2,130 · he pays ~$981', '$1,240 benefits left · resets in 94 days', 'Opened his plan 3 times'],
        whatToSay: [
          { label: 'Open with', body: 'His question: “Is the crown covered now? Can I do it in January?”' },
          { label: 'The money', body: 'Insurance pays about half; he has $1,240 left this year — enough for both.' },
          { label: 'Why now', body: 'The crack. January is possible and costs about the same.' },
        ],
        dont: 'promise an exact price · don’t add anything clinical Dr. Carter hasn’t written',
      },
      {
        type: 'balance', title: '$210 balance',
        whyLine: '$210 · filling 28 Jul · 64 days · ELVA called 22 Sep, no answer',
        chips: ['Filling 28 Jul · insurance paid $390', '64 days', 'Payment link sent 3 times, not opened'],
        whatToSay: [{ label: 'Say', body: '“There’s $210 left from your filling in July — I can text you the payment link now.”' }],
        dont: 'mention collections, credit or anything that sounds like a threat',
      },
    ],
    timeline: [
      { at: '28 Sep 7:48p', body: 'Replied to the plan text:', quote: 'Is the crown covered now? Last time it was too much. Can I do it in January instead?' },
      { at: '28 Sep 7:42p', body: 'Opened his plan · viewed the x-ray' },
      { at: '22 Sep', body: 'ELVA called about the $210 — no answer, left no message' },
      { at: '15 Sep', body: 'Balance text with the payment link · not opened' },
      { at: '13 Sep', body: 'Plan text, step 3' },
    ],
  },
  {
    id: 'aaron-vasquez', name: 'Aaron Vasquez', phone: '(210) 555-0158',
    cardIdentity: '46 · since 2011 · Aetna PPO', group: 'today', tries: [], callingBy: 'Maria',
    reasons: [{
      type: 'balance', title: '$1,340 balance',
      whyLine: '$1,340 · over $1,000, so a person calls · 2 texts, 1 email, link not opened',
      chips: ['$1,340', 'Over $1,000 — a person calls', '2 texts · 1 email · link not opened'],
      whatToSay: [{ label: 'Say', body: '“There’s $1,340 outstanding — I can take a card now or set up a plan.”' }],
      dont: say('balance').dont,
    }],
    timeline: [{ at: '26 Sep', body: 'Email with the payment link · not opened' }, { at: '20 Sep', body: 'Balance text · not opened' }],
  },
  {
    id: 'leila-moradi', name: 'Leila Moradi', phone: '(210) 555-0184', bestTime: 'best in the evening',
    cardIdentity: '34 · since 2020 · Delta Dental PPO · best in the evening', group: 'today',
    tries: [{ n: 1, at: 'Tue 10:14', who: 'Dana', outcome: 'voicemail' }],
    reasons: [{
      type: 'invisalign', title: 'Clear aligners', quote: 'Does insurance cover any of it?',
      whyLine: 'Replied 28 Sep: {q} · $1,500 ortho benefit',
      chips: ['$1,500 ortho benefit, unused', 'Replied to the day-11 email'],
      whatToSay: [{ label: 'Open with', body: 'Her question about insurance.' }, { label: 'The money', body: 'She has $1,500 of unused orthodontic benefit; a consult gives the exact number.' }],
      dont: say('invisalign').dont,
    }],
    timeline: [{ at: '28 Sep', body: 'Replied to the day-11 email:', quote: 'Does insurance cover any of it?' }],
  },
  {
    id: 'grace-whitfield', name: 'Grace Whitfield', phone: '(210) 555-0127',
    cardIdentity: '29 · since 2018 · MetLife PPO', group: 'today', tries: [],
    reasons: [
      {
        type: 'invisalign', title: 'Clear aligners', quote: 'What does it cost per month?',
        whyLine: 'Replied 26 Sep: {q}',
        chips: ['$2,000 ortho benefit, unused', 'Asked about monthly payments'],
        whatToSay: [{ label: 'Open with', body: 'Her question about the monthly cost.' }, { label: 'The money', body: 'A consult gives the exact number; monthly plans are available.' }],
        dont: say('invisalign').dont,
      },
      {
        type: 'recall', title: 'Cleaning overdue',
        whyLine: 'Cleaning 9 months overdue · 2 texts, no reply',
        chips: ['9 months overdue', '2 texts · no reply'],
        whatToSay: [{ label: 'Say', body: 'She’s due for a cleaning and you can find a time now.' }],
        dont: say('recall').dont,
      },
    ],
    timeline: [{ at: '26 Sep', body: 'Replied:', quote: 'What does it cost per month?' }, { at: '10 Sep', body: 'Recall text · no reply' }],
  },
  {
    id: 'daniel-ortiz', name: 'Daniel Ortiz', phone: '(210) 555-0163', bestTime: 'best in the morning',
    cardIdentity: '37 · since 2015 · Guardian PPO · best in the morning', group: 'today',
    tries: [{ n: 1, at: 'Tue 3:20 pm', who: 'Maria', outcome: 'no answer' }],
    reasons: [{
      type: 'invisalign', title: 'Clear aligners',
      whyLine: 'Clicked the consult link 29 Sep and stopped at choosing a time',
      chips: ['$1,000 ortho benefit', 'Clicked the consult link · didn’t book'],
      whatToSay: [{ label: 'Open with', body: 'He clicked to book and stopped — offer to find a time now.' }],
      dont: say('invisalign').dont,
    }],
    timeline: [{ at: '29 Sep 12:40p', body: 'Clicked the consult link · stopped at choosing a time' }],
  },
  // Today, the "Show 4 more" (11 total).
  mk('rosa-kim', 'Rosa Kim', '(210) 555-0190', 'today', 'treatment', 'Onlay #19 · $1,180', ['$1,180 · she pays ~$540']),
  mk('ben-carter', 'Ben Carter', '(210) 555-0111', 'today', 'balance', '$430 · crown 2 Aug · 59 days', ['$430', '59 days']),
  mk('nia-adams', 'Nia Adams', '(210) 555-0145', 'today', 'recall', 'Cleaning 7 months overdue · 3 texts, no reply', ['7 months overdue']),
  mk('sam-poole', 'Sam Poole', '(210) 555-0172', 'today', 'treatment', 'Root canal #30 diagnosed 20 Sep · $1,320', ['$1,320 · he pays ~$610']),
  // This week (8).
  mk('hannah-cole', 'Hannah Cole', '(210) 555-0155', 'week', 'invisalign', 'Asked the Invis-Line about timing before graduation', ['$1,500 ortho benefit']),
  mk('alma-reyes', 'Alma Reyes', '(210) 555-0138', 'week', 'treatment', 'Crown #3 diagnosed 18 Sep · $1,240', ['$1,240']),
  mk('callie-nguyen', 'Callie Nguyen', '(210) 555-0166', 'week', 'treatment', 'Bridge diagnosed 16 Sep · $3,400', ['$3,400']),
  mk('rita-gomez', 'Rita Gomez', '(210) 555-0129', 'week', 'balance', '$520 · deep cleaning 6 Aug · 55 days', ['$520']),
  mk('omar-haddad', 'Omar Haddad', '(210) 555-0181', 'week', 'treatment', 'Two fillings diagnosed 14 Sep · $560', ['$560']),
  mk('priya-nair', 'Priya Nair', '(210) 555-0193', 'week', 'balance', '$180 · exam 10 Aug · 51 days', ['$180']),
  mk('wes-turner', 'Wes Turner', '(210) 555-0104', 'week', 'recall', 'Cleaning 6 months overdue · 1 text', ['6 months overdue']),
  mk('faye-long', 'Faye Long', '(210) 555-0117', 'week', 'treatment', 'Crown #12 diagnosed 12 Sep · $1,300', ['$1,300']),
  // Later (5) — each shows the day it comes back.
  { ...mk('marcus-reid', 'Marcus Reid', '(210) 555-0148', 'later', 'balance', '$640 · call back requested', ['$640']), comesBack: 'Comes back 6 Oct' },
  { ...mk('gordon-vance', 'Gordon Vance', '(210) 555-0159', 'later', 'treatment', 'Crown #8 · thinking, call back', ['$1,410']), comesBack: 'Comes back 7 Oct' },
  { ...mk('junie-park', 'Junie Park', '(210) 555-0170', 'later', 'balance', '$95 · call back requested', ['$95']), comesBack: 'Comes back 9 Oct' },
  { ...mk('ivy-chen', 'Ivy Chen', '(210) 555-0182', 'later', 'invisalign', 'Wants to talk it over — call back', ['$1,500 ortho benefit']), comesBack: 'Comes back 10 Oct' },
  { ...mk('hugo-dane', 'Hugo Dane', '(210) 555-0195', 'later', 'recall', 'Cleaning due · call back requested', ['due']), comesBack: 'Comes back 14 Oct' },
];

/** A compact row for the lighter (non-hero) patients. */
function mk(id: string, name: string, phone: string, group: CallRow['group'], type: CallReasonType, whyLine: string, chips: string[]): CallRow {
  const meta = say(type);
  return {
    id, name, phone, cardIdentity: name, group, tries: [],
    reasons: [{ type, title: whyLine.split(' · ')[0] ?? whyLine, whyLine, chips, whatToSay: meta.whatToSay, dont: meta.dont }],
    timeline: [{ at: 'recent', body: whyLine }],
  };
}

/* ---- filter counts (derived, so they reconcile — §1) --------------------- */

export function callFilterCounts(): Record<CallFilter, number> {
  const counts: Record<CallFilter, number> = { all: CALL_ROWS.length, asked: 0, treatment: 0, invisalign: 0, balance: 0, recall: 0 };
  for (const row of CALL_ROWS) {
    for (const r of row.reasons) {
      counts[r.type] += 1;
      if (r.asked) counts.asked += 1;
    }
  }
  return counts;
}

/** Rows matching a filter (a two-reason patient shows under both). */
export function callRowsFor(filter: CallFilter): CallRow[] {
  if (filter === 'all') return CALL_ROWS;
  if (filter === 'asked') return CALL_ROWS.filter((r) => r.reasons.some((x) => x.asked));
  return CALL_ROWS.filter((r) => r.reasons.some((x) => x.type === filter));
}

/* ---- the "someone is on this call" store (§3 / §4) ----------------------- */

export const callingStore: Record<string, string> = { 'aaron-vasquez': 'Maria' };
